import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { formatBytes, formatDate } from 'client/js/util/formatters';

const FileItem = ({ item, isSelected, onSelect, onItemClick, onDoubleClick, onContextMenu, paneId, currentPath, selectedItems, onDropOnItem, onRename, isRenaming, onRenameCancel, onRenameSubmit }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [newName, setNewName] = useState(item.name);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isRenaming) {
      setNewName(item.name);
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }
  }, [isRenaming, item.name]);

  const handleKeyDown = (e) => {
    // Stop propagation to prevent FileBrowser navigation (e.g. Backspace)
    e.stopPropagation();
    
    if (e.key === 'Enter') {
      e.preventDefault();
      onRenameSubmit(item, newName);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onRenameCancel();
    }
  };

  const handleBlur = () => {
    onRenameSubmit(item, newName);
  };

  const getIcon = () => {
    if (item.type === 'directory') {
      return 'fa-folder';
    }
    return 'fa-file';
  };

  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move';

    // If the current item is selected, drag all selected items
    // Otherwise, just drag this item
    const itemsToDrag = isSelected && selectedItems.length > 0
      ? selectedItems
      : [item];

    e.dataTransfer.setData('application/json', JSON.stringify({
      items: itemsToDrag,
      sourcePaneId: paneId,
      sourcePath: currentPath
    }));
  };

  const handleDragOver = (e) => {
    if (item.type === 'directory') {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    if (item.type !== 'directory') return;

    if (onDropOnItem) {
      onDropOnItem(e, item);
    }
  };

  return (
    <tr
      className={`file-item ${isSelected ? 'selected' : ''} ${isDragOver ? 'drag-over' : ''}`}
      onClick={onItemClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={(e) => {
        e.stopPropagation();
        onContextMenu(e);
      }}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <td>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      </td>
      <td>
        <i className={`fa ${getIcon()}`} />
      </td>
      <td className="file-name">
        {isRenaming ? (
          <input
            ref={inputRef}
            type="text"
            className="form-control form-control-sm"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          item.name
        )}
      </td>
      <td className="text-right">{item.type === 'directory' ? '-' : formatBytes(item.size)}</td>
      <td className="text-monospace">{item.mode}</td>
      <td>{formatDate(item.mtime)}</td>
    </tr>
  );
};

FileItem.propTypes = {
  item: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func.isRequired,
  paneId: PropTypes.string.isRequired,
  currentPath: PropTypes.string.isRequired,
  selectedItems: PropTypes.array.isRequired,
  onDropOnItem: PropTypes.func,
  isRenaming: PropTypes.bool,
  onRenameCancel: PropTypes.func,
  onRenameSubmit: PropTypes.func,
};

export default FileItem;